import React, { useState, useEffect, useCallback } from "react";
import { useHotel } from "../contexts/HotelContext";
import { useToast } from "../contexts/ToastContext";
import Layout from "../components/Layout";
import apiClient from "../services/apiClient";
import { updateUserModules, getSeatUsage } from "../services/tenantApi";
import type { SeatUsage } from "../services/tenantApi";
import { getSupportEmail } from "../utils/domainConfig";
import { MODULE_DISPLAY_NAMES } from "../lib/modules";

interface TenantMember {
  id: string;
  userId: string;
  tenantId: string;
  tenantRole: string; // Role in tenant (tenant_admin or tenant_user)
  globalRole: string; // Global system role
  email: string;
  firstName: string;
  lastName: string;
  isActive: boolean;
  hasPassword: boolean; // true if user has password set
  enabledModules?: string[]; // per-user privilege modules
  joinedAt: string;
  updatedAt: string;
  createdAt: string;
}

interface UserFormData {
  email: string;
  firstName: string;
  lastName: string;
  role: string;
}

const TENANT_ROLES = {
  ADMIN: "tenant_admin",
  USER: "tenant_user",
} as const;

const DEFAULT_FORM_DATA: UserFormData = {
  email: "",
  firstName: "",
  lastName: "",
  role: TENANT_ROLES.USER,
};

const ROLE_LABELS: Record<string, string> = {
  [TENANT_ROLES.ADMIN]: "Administrator",
  [TENANT_ROLES.USER]: "Użytkownik",
};

const ROLE_DESCRIPTIONS: Record<string, string> = {
  [TENANT_ROLES.ADMIN]: "Może zarządzać użytkownikami i subskrypcją organizacji",
  [TENANT_ROLES.USER]: "Może korzystać z funkcji systemu zgodnie z uprawnieniami",
};

const MAX_ERROR_MESSAGE_LENGTH = 200;

/**
 * Privilege modules — namespace admin:* only (per spec AC-4d).
 * Capability modules (grants:*, hotel:*, api-keys:*) are blocked by backend DTO.
 * Sync with backend: apps/backend/src/tenant/dto/update-per-user-modules.dto.ts
 */
const ALLOWED_PRIVILEGE_MODULES: string[] = [
  "admin:kiedynabory:read",
  "admin:kiedynabory:write",
  "admin:kiedynabory:rozliczenia-paulina",
  "admin:kiedynabory:rozliczenia-tenants",
  "admin:kiedynabory:*",
];

function getErrorMessage(error: unknown, fallback: string): string {
  let message = fallback;
  if (error && typeof error === "object" && "response" in error) {
    const axiosError = error as { response?: { data?: { message?: string } }; message?: string };
    message = axiosError.response?.data?.message || axiosError.message || fallback;
  } else if (error instanceof Error) {
    message = error.message;
  }
  return message.length > MAX_ERROR_MESSAGE_LENGTH
    ? message.slice(0, MAX_ERROR_MESSAGE_LENGTH) + "..."
    : message;
}

const RoleSelectTooltip: React.FC = () => (
  <div className="group relative ml-1">
    <button type="button" tabIndex={0} className="focus:outline-none">
      <svg
        className="w-4 h-4 text-neutral-400 cursor-help"
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path
          fillRule="evenodd"
          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z"
          clipRule="evenodd"
        />
      </svg>
    </button>
    <div className="invisible group-hover:visible group-focus-within:visible absolute left-6 top-0 w-64 bg-neutral-800 text-white text-xs rounded-lg p-3 z-50 shadow-lg text-left">
      <div className="mb-2">
        <div className="font-semibold">Administrator</div>
        <div className="text-neutral-300">
          {ROLE_DESCRIPTIONS[TENANT_ROLES.ADMIN]}
        </div>
      </div>
      <div>
        <div className="font-semibold">Użytkownik</div>
        <div className="text-neutral-300">
          {ROLE_DESCRIPTIONS[TENANT_ROLES.USER]}
        </div>
      </div>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// PrivilegesModal — per-user privilege editor (AC-4)
// ---------------------------------------------------------------------------
interface PrivilegesModalProps {
  member: TenantMember;
  tenantId: string;
  onClose: () => void;
  onSaved: () => void;
}

const PrivilegesModal: React.FC<PrivilegesModalProps> = ({
  member,
  tenantId,
  onClose,
  onSaved,
}) => {
  const { addToast } = useToast();
  const initialModules = (member.enabledModules ?? []).filter((m) => m.startsWith("admin:"));
  const [selectedModules, setSelectedModules] = useState<string[]>(initialModules);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const toggleModule = (moduleKey: string) => {
    setSelectedModules((prev) =>
      prev.includes(moduleKey)
        ? prev.filter((m) => m !== moduleKey)
        : [...prev, moduleKey]
    );
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    // No-op guard: skip API call if nothing changed
    const sorted = (arr: string[]) => [...arr].sort().join(",");
    if (sorted(selectedModules) === sorted(initialModules)) {
      addToast({ type: "info", message: "Brak zmian do zapisania." });
      onClose();
      return;
    }

    // Confirmation when removing existing privileges
    const removed = initialModules.filter((m) => !selectedModules.includes(m));
    if (removed.length > 0) {
      const confirmed = window.confirm(
        `Usuniesz ${removed.length} uprawnienie(-ia/-ień). Kontynuować?`
      );
      if (!confirmed) return;
    }

    setIsSubmitting(true);

    try {
      await updateUserModules(tenantId, member.userId, selectedModules);

      // AC-4a: persistent re-login feedback (30s visible)
      addToast({
        type: "info",
        message: "Zmiany będą widoczne po ponownym zalogowaniu użytkownika.",
        duration: 30000,
      });

      onSaved();
      onClose();
    } catch (error: unknown) {
      addToast({
        type: "error",
        message: getErrorMessage(error, "Błąd zapisu uprawnień użytkownika"),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasNoPrivileges = selectedModules.length === 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        {/* AC-4c: header with distinct color (indigo = visually different from primary) */}
        <div className="px-6 py-4 border-b border-indigo-100 bg-indigo-50 rounded-t-lg">
          <h3 className="text-lg font-semibold text-indigo-900">
            Przywileje osobiste użytkownika
          </h3>
          <p className="text-sm text-indigo-700 mt-1">
            {member.firstName} {member.lastName} &lt;{member.email}&gt;
          </p>
        </div>

        <form onSubmit={handleSave} className="p-6">
          {/* AC-4c: semantic separation — note */}
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
            Moduły organizacji zarządzasz w ustawieniach organizacji. Tu
            nadajesz osobiste uprawnienia konkretnemu użytkownikowi.
          </div>

          {/* AC-4d: only admin:* modules */}
          <div className="space-y-2 mb-4">
            <p className="text-xs font-medium text-neutral-500 uppercase tracking-wider mb-2">
              Uprawnienia osobiste (admin:*)
            </p>
            {ALLOWED_PRIVILEGE_MODULES.map((moduleKey) => (
              <label
                key={moduleKey}
                className="flex items-center gap-3 p-2 rounded hover:bg-neutral-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  value={moduleKey}
                  checked={selectedModules.includes(moduleKey)}
                  onChange={() => toggleModule(moduleKey)}
                  className="h-4 w-4 text-indigo-600 border-neutral-300 rounded focus:ring-indigo-500"
                />
                <div>
                  <div className="text-sm font-medium text-neutral-900">
                    {MODULE_DISPLAY_NAMES[moduleKey] || moduleKey}
                  </div>
                  <div className="text-xs text-neutral-500">{moduleKey}</div>
                </div>
              </label>
            ))}
          </div>

          {/* AC-4e: empty state */}
          {hasNoPrivileges && (
            <div className="mb-4 p-3 bg-neutral-50 border border-neutral-200 rounded-lg text-sm text-neutral-600 text-center">
              Ten użytkownik nie ma żadnych osobistych uprawnień.
            </div>
          )}

          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-neutral-700 bg-neutral-100 hover:bg-neutral-200 rounded-lg transition-colors"
            >
              Anuluj
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              {isSubmitting ? "Zapisywanie..." : "Zapisz"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
const TenantUsers: React.FC = () => {
  const { user } = useHotel();
  const { addToast } = useToast();
  const [users, setUsers] = useState<TenantMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPrivilegesModal, setShowPrivilegesModal] = useState(false);
  const [editingUser, setEditingUser] = useState<TenantMember | null>(null);
  const [privilegesUser, setPrivilegesUser] = useState<TenantMember | null>(null);
  const [formData, setFormData] = useState<UserFormData>(DEFAULT_FORM_DATA);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resendingUserId, setResendingUserId] = useState<string | null>(null);

  // Seat limit (spec 2026-06-16-tenant-seat-limit-enforcement)
  const [seats, setSeats] = useState<SeatUsage | null>(null);
  const [seatsLoading, setSeatsLoading] = useState(true);
  const [seatsError, setSeatsError] = useState(false);

  const supportEmail = getSupportEmail();

  // Unified seat-limit contact message — single source of truth for all three locations
  // (toast after 403, empty state, amber banner).
  const seatLimitContactMessage = `Osiągnięto limit stanowisk planu. Aby dodać więcej użytkowników, skontaktuj się z naszym wsparciem: ${supportEmail}`;

  // AC-4f: global admin check — only role==="admin" can manage per-user privileges
  const isGlobalAdmin = user?.role === "admin";

  // Get current tenant ID from user's tenants
  const currentTenant = user?.tenants?.[0];
  const tenantId = currentTenant?.id;

  const fetchUsers = useCallback(async () => {
    if (!tenantId) return;

    try {
      const { data } = await apiClient.get(`/tenants/${tenantId}/users`);
      setUsers(data);
    } catch (error) {
      addToast({ type: "error", message: "Błąd ładowania użytkowników" });
    } finally {
      setLoading(false);
    }
  }, [tenantId, addToast]);

  const fetchSeats = useCallback(async () => {
    if (!tenantId) return;

    setSeatsLoading(true);
    setSeatsError(false);
    try {
      const data = await getSeatUsage(tenantId);
      setSeats(data);
    } catch {
      // Fail-open: backend still enforces 403 on the write path. Hide the
      // counter and let the user try — they get a clear 403 toast if blocked.
      setSeatsError(true);
      setSeats(null);
      addToast({
        type: "error",
        message: "Nie udało się sprawdzić limitu stanowisk",
      });
    } finally {
      setSeatsLoading(false);
    }
  }, [tenantId, addToast]);

  useEffect(() => {
    if (tenantId) {
      fetchUsers();
      fetchSeats();
    } else {
      setLoading(false);
      setSeatsLoading(false);
    }
  }, [tenantId, fetchUsers, fetchSeats]);

  // Derived seat-limit state.
  const hasLimit = seats != null && seats.maxSeats != null;
  const seatUsed = seats?.used ?? 0;
  const seatMax = seats?.maxSeats ?? null;
  const seatRemaining = seats?.remaining ?? null;
  // remaining <= 0 ⇒ limit reached (covers used == max and used > max / eitt.pl).
  const seatLimitReached =
    hasLimit && seatRemaining !== null && seatRemaining <= 0;
  // Fail-safe while loading (disabled), fail-open on error (enabled).
  const addDisabled = seatsLoading || seatLimitReached;

  const handleResendInvite = async (userId: string, userEmail: string) => {
    if (!tenantId || resendingUserId) return;

    // Confirmation dialog
    const confirmed = window.confirm(
      `Czy na pewno chcesz wysłać ponownie zaproszenie do ${userEmail}?\n\nPo wysłaniu poprzedni link aktywacyjny przestanie działać.`
    );
    if (!confirmed) return;

    setResendingUserId(userId);

    try {
      await apiClient.post(`/tenants/${tenantId}/users/${userId}/resend-invite`);
      addToast({ type: "success", message: `Zaproszenie do ${userEmail} zostało wysłane ponownie!` });
      // 5-second cooldown to prevent repeated sends
      setTimeout(() => {
        setResendingUserId(null);
      }, 5000);
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } }).response?.status;
      let errorMessage = "Błąd wysyłania zaproszenia";

      if (status === 429) {
        errorMessage = "Zbyt wiele prób. Spróbuj ponownie za godzinę.";
      } else if (status === 400) {
        errorMessage = getErrorMessage(error, "Użytkownik ma już ustawione hasło.");
      } else if (status === 404) {
        errorMessage = "Użytkownik nie należy do tej organizacji.";
      } else {
        errorMessage = getErrorMessage(error, errorMessage);
      }

      addToast({ type: "error", message: errorMessage });
      setResendingUserId(null);
    }
  };

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tenantId) return;

    setIsSubmitting(true);

    try {
      await apiClient.post(`/tenants/${tenantId}/users/create`, formData);
      addToast({ type: "success", message: "Użytkownik dodany pomyślnie!" });
      setShowAddModal(false);
      setFormData(DEFAULT_FORM_DATA);
      fetchUsers();
      fetchSeats();
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } }).response
        ?.status;
      if (status === 403) {
        // Seat limit hit (race / UI bypass). Close modal first so the user
        // lands on the view with the amber banner (which has a clickable mailto:).
        setShowAddModal(false);
        setFormData(DEFAULT_FORM_DATA);
        addToast({
          type: "error",
          message: seatLimitContactMessage,
        });
        fetchSeats(); // refresh counter to reflect current usage
      } else {
        addToast({
          type: "error",
          message: getErrorMessage(error, "Błąd dodawania użytkownika"),
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser || !tenantId) return;

    setIsSubmitting(true);

    try {
      await apiClient.patch(
        `/tenants/${tenantId}/users/${editingUser.userId}`,
        {
          role: formData.role,
        }
      );
      addToast({ type: "success", message: "Użytkownik zaktualizowany pomyślnie!" });
      setShowEditModal(false);
      setEditingUser(null);
      setFormData(DEFAULT_FORM_DATA);
      fetchUsers();
    } catch (error: unknown) {
      addToast({ type: "error", message: getErrorMessage(error, "Błąd aktualizacji użytkownika") });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (
      !window.confirm(
        "Czy na pewno chcesz usunąć dostęp tego użytkownika do organizacji?"
      )
    ) {
      return;
    }

    if (!tenantId) return;

    try {
      await apiClient.delete(`/tenants/${tenantId}/users/${userId}`);
      addToast({ type: "success", message: "Dostęp użytkownika usunięty pomyślnie!" });
      fetchUsers();
      fetchSeats();
    } catch (error: unknown) {
      addToast({ type: "error", message: getErrorMessage(error, "Błąd usuwania dostępu użytkownika") });
    }
  };

  const openEditModal = (member: TenantMember) => {
    setEditingUser(member);
    setFormData({
      email: member.email,
      firstName: member.firstName,
      lastName: member.lastName,
      role: member.tenantRole,
    });
    setShowEditModal(true);
  };

  const openPrivilegesModal = (member: TenantMember) => {
    setPrivilegesUser(member);
    setShowPrivilegesModal(true);
  };

  const closeModals = () => {
    setShowAddModal(false);
    setShowEditModal(false);
    setShowPrivilegesModal(false);
    setEditingUser(null);
    setPrivilegesUser(null);
    setFormData(DEFAULT_FORM_DATA);
  };

  if (loading) {
    return (
      <Layout showBackButton={true}>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-neutral-600">Ładowanie...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!tenantId) {
    return (
      <Layout showBackButton={true}>
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
            <div className="text-center">
              <svg
                className="h-12 w-12 text-neutral-400 mx-auto mb-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v2m0 0v2m0-2h2m-2 0H10m9-9a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <h2 className="text-xl font-semibold text-neutral-900 mb-2">
                Brak dostępu do organizacji
              </h2>
              <p className="text-neutral-600">
                Nie masz dostępu do żadnej organizacji.
              </p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout showBackButton={true}>
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm border border-neutral-200">
          <div className="p-6">
            <div className="mb-8">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-lg font-semibold text-neutral-900">
                    Zarządzanie użytkownikami
                  </h2>
                  <p className="text-sm text-neutral-600 mt-1">
                    Zarządzaj użytkownikami dla organizacji{" "}
                    {currentTenant?.name || ""}
                  </p>
                </div>
                <div className="flex flex-col items-end gap-2">
                  {/* Seat counter — fail-safe skeleton while loading, hidden on
                      error (fail-open) or when the plan has no limit (M4). */}
                  {seatsLoading ? (
                    <div
                      className="h-4 w-28 bg-neutral-100 rounded animate-pulse"
                      aria-hidden="true"
                    />
                  ) : (
                    hasLimit &&
                    !seatsError && (
                      <div
                        role="note"
                        aria-label="Informacja o limicie stanowisk"
                        className={`text-sm ${
                          seatLimitReached
                            ? "text-error-700 font-medium"
                            : "text-neutral-600"
                        }`}
                      >
                        {seatUsed} / {seatMax} stanowisk
                      </div>
                    )
                  )}
                  <button
                    onClick={() => setShowAddModal(true)}
                    disabled={addDisabled}
                    aria-label={
                      seatsLoading
                        ? "Dodaj użytkownika — trwa sprawdzanie limitu stanowisk"
                        : seatLimitReached
                        ? `Nie można dodać użytkownika — osiągnięto limit ${seatMax} stanowisk planu`
                        : undefined
                    }
                    title={seatsLoading ? "Sprawdzanie limitu stanowisk..." : undefined}
                    className="bg-primary-600 hover:bg-primary-700 text-white font-semibold py-2 px-4 rounded-lg shadow-sm transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Dodaj użytkownika
                  </button>
                </div>
              </div>

              {/* Seat limit reached — two message variants (H9) + mailto support */}
              {seatLimitReached && (
                <div
                  role="alert"
                  aria-live="polite"
                  className="mb-6 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800"
                >
                  {seatUsed > (seatMax ?? 0) ? (
                    <>
                      Twoja organizacja przekroczyła limit {seatMax} stanowisk
                      (aktualnie {seatUsed}).{" "}
                    </>
                  ) : (
                    <>Osiągnięto limit {seatMax} stanowisk planu. </>
                  )}
                  Aby dodać więcej użytkowników, skontaktuj się z naszym
                  wsparciem:{" "}
                  <a
                    href={`mailto:${supportEmail}`}
                    className="font-medium underline"
                  >
                    {supportEmail}
                  </a>
                  .
                </div>
              )}

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-neutral-200">
                  <thead className="bg-neutral-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                        Użytkownik
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                        Rola
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                        Status
                      </th>
                      {/* AC-4b: Moduły column for discoverability */}
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                        Moduły
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                        Akcje
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-neutral-200">
                    {users.length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-6 py-12 text-center text-neutral-500">
                          {seatLimitReached
                            ? "Osiągnięto limit stanowisk planu."
                            : 'Brak użytkowników. Kliknij "Dodaj użytkownika" aby zaprosić pierwszą osobę.'}
                        </td>
                      </tr>
                    )}
                    {users.map((member) => {
                      const privilegeCount = (member.enabledModules ?? []).filter(
                        (m) => m.startsWith("admin:")
                      ).length;

                      return (
                        <tr key={member.userId}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="h-8 w-8 bg-primary-100 rounded-full flex items-center justify-center">
                                <span className="text-sm font-medium text-primary-700">
                                  {member.firstName.charAt(0)}
                                </span>
                              </div>
                              <div className="ml-3">
                                <div className="text-sm font-medium text-neutral-900">
                                  {member.firstName} {member.lastName}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">
                            {member.email}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                member.tenantRole === TENANT_ROLES.ADMIN
                                  ? "bg-purple-100 text-purple-800"
                                  : "bg-blue-100 text-blue-800"
                              }`}
                            >
                              {ROLE_LABELS[member.tenantRole] || member.tenantRole}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {!member.hasPassword ? (
                              <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-warning-100 text-warning-800">
                                Oczekuje
                              </span>
                            ) : member.isActive ? (
                              <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-success-100 text-success-800">
                                Aktywny
                              </span>
                            ) : (
                              <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-neutral-100 text-neutral-800">
                                Nieaktywny
                              </span>
                            )}
                          </td>
                          {/* AC-4b: privilege module count badge */}
                          <td className="px-6 py-4 whitespace-nowrap">
                            {privilegeCount > 0 ? (
                              <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-indigo-100 text-indigo-800">
                                {privilegeCount} admin:*
                              </span>
                            ) : (
                              <span className="text-xs text-neutral-400">—</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            {!member.hasPassword && (
                              <button
                                onClick={() => handleResendInvite(member.userId, member.email)}
                                disabled={resendingUserId === member.userId}
                                className="text-warning-600 hover:text-warning-900 mr-3 disabled:opacity-50 disabled:cursor-not-allowed"
                                aria-label={`Wyślij ponownie zaproszenie do ${member.email}`}
                              >
                                {resendingUserId === member.userId
                                  ? "Wysyłanie..."
                                  : "Wyślij ponownie"}
                              </button>
                            )}
                            <button
                              onClick={() => openEditModal(member)}
                              className="text-primary-600 hover:text-primary-900 mr-3"
                            >
                              Edytuj
                            </button>
                            {/* AC-4f: separate "Przywileje" button — disabled for tenant_admin */}
                            {isGlobalAdmin ? (
                              <button
                                onClick={() => openPrivilegesModal(member)}
                                className="text-indigo-600 hover:text-indigo-900 mr-3"
                              >
                                Przywileje
                              </button>
                            ) : (
                              <button
                                disabled
                                title="Tylko administrator globalny"
                                className="text-neutral-400 cursor-not-allowed mr-3 disabled:opacity-50"
                              >
                                Przywileje
                              </button>
                            )}
                            {member.userId !== user?.id && (
                              <button
                                onClick={() => handleDeleteUser(member.userId)}
                                className="text-error-600 hover:text-error-900"
                              >
                                Usuń dostęp
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add User Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-neutral-200">
              <h3 className="text-lg font-semibold text-neutral-900">
                Dodaj nowego użytkownika
              </h3>
            </div>
            <form onSubmit={handleAddUser} className="p-6">
              <div className="space-y-4">
                <div>
                  <label htmlFor="add-email" className="block text-sm font-medium text-neutral-700 mb-2">
                    Email
                  </label>
                  <input
                    id="add-email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="uzytkownik@example.com"
                  />
                </div>
                <div>
                  <label htmlFor="add-firstName" className="block text-sm font-medium text-neutral-700 mb-2">
                    Imię
                  </label>
                  <input
                    id="add-firstName"
                    type="text"
                    required
                    value={formData.firstName}
                    onChange={(e) =>
                      setFormData({ ...formData, firstName: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Jan"
                  />
                </div>
                <div>
                  <label htmlFor="add-lastName" className="block text-sm font-medium text-neutral-700 mb-2">
                    Nazwisko
                  </label>
                  <input
                    id="add-lastName"
                    type="text"
                    required
                    value={formData.lastName}
                    onChange={(e) =>
                      setFormData({ ...formData, lastName: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Kowalski"
                  />
                </div>
                <div>
                  <div className="flex items-center mb-2">
                    <label htmlFor="add-role" className="block text-sm font-medium text-neutral-700">
                      Rola
                    </label>
                    <RoleSelectTooltip />
                  </div>
                  <select
                    id="add-role"
                    value={formData.role}
                    onChange={(e) =>
                      setFormData({ ...formData, role: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value={TENANT_ROLES.USER}>Użytkownik</option>
                    <option value={TENANT_ROLES.ADMIN}>Administrator</option>
                  </select>
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={closeModals}
                  className="px-4 py-2 text-neutral-700 bg-neutral-100 hover:bg-neutral-200 rounded-lg transition-colors"
                >
                  Anuluj
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || seatLimitReached}
                  className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors disabled:opacity-50"
                >
                  {isSubmitting ? "Dodawanie..." : "Dodaj użytkownika"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-neutral-200">
              <h3 className="text-lg font-semibold text-neutral-900">
                Edytuj rolę użytkownika
              </h3>
            </div>
            <form onSubmit={handleEditUser} className="p-6">
              <div className="space-y-4">
                <div>
                  <label htmlFor="edit-email" className="block text-sm font-medium text-neutral-700 mb-2">
                    Email
                  </label>
                  <input
                    id="edit-email"
                    type="email"
                    disabled
                    value={formData.email}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg bg-neutral-50 text-neutral-500"
                  />
                </div>
                <div>
                  <label htmlFor="edit-firstName" className="block text-sm font-medium text-neutral-700 mb-2">
                    Imię
                  </label>
                  <input
                    id="edit-firstName"
                    type="text"
                    disabled
                    value={formData.firstName}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg bg-neutral-50 text-neutral-500"
                  />
                </div>
                <div>
                  <label htmlFor="edit-lastName" className="block text-sm font-medium text-neutral-700 mb-2">
                    Nazwisko
                  </label>
                  <input
                    id="edit-lastName"
                    type="text"
                    disabled
                    value={formData.lastName}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg bg-neutral-50 text-neutral-500"
                  />
                  <p className="mt-1 text-xs text-neutral-400">
                    Dane osobowe może zmienić tylko sam użytkownik.
                  </p>
                </div>
                <div>
                  <div className="flex items-center mb-2">
                    <label htmlFor="edit-role" className="block text-sm font-medium text-neutral-700">
                      Rola
                    </label>
                    <RoleSelectTooltip />
                  </div>
                  <select
                    id="edit-role"
                    value={formData.role}
                    onChange={(e) =>
                      setFormData({ ...formData, role: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value={TENANT_ROLES.USER}>Użytkownik</option>
                    <option value={TENANT_ROLES.ADMIN}>Administrator</option>
                  </select>
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={closeModals}
                  className="px-4 py-2 text-neutral-700 bg-neutral-100 hover:bg-neutral-200 rounded-lg transition-colors"
                >
                  Anuluj
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors disabled:opacity-50"
                >
                  {isSubmitting ? "Aktualizacja..." : "Aktualizuj rolę"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Privileges Modal — per-user privilege editor (AC-4) */}
      {showPrivilegesModal && privilegesUser && tenantId && (
        <PrivilegesModal
          member={privilegesUser}
          tenantId={tenantId}
          onClose={closeModals}
          onSaved={fetchUsers}
        />
      )}
    </Layout>
  );
};

export default TenantUsers;
